/**
 * om-cart-upsell.js  (om- = Omnana customization)
 *
 * Behavior for the cart-drawer "You may also like" mini row rendered by the
 * 'upsell-products' section block (snippets/om-cart-upsell.liquid).
 *
 * This file manages the ROW REFRESH and the prev/next arrows. Adding to cart
 * needs no custom JS:
 * single-variant cards use a [data-add-to-cart] submit inside a product form,
 * and multi-variant cards open the theme's quick-add modal (mini-PDP with the
 * product page's variant picker + "Add to cart") - both handled end to end by
 * Broadcast's QuickAddProduct / CartItems in theme.dev.js.
 *
 * Refresh: CartItems.build() only swaps its own holders ([data-upsell-products]
 * etc.), never our container - so after every cart rebuild (signalled by
 * `theme:cart:change`) we refetch sections/om-api-cart-upsell.liquid via the
 * Section Rendering API to get a fresh, deduped product list. Block settings
 * are not readable in that section, so the container's data attributes carry
 * the config:
 * - data-om-source="collection": fetch data-om-collection-url with
 *   ?section_id, the routed collection provides the products.
 * - data-om-source="recommendations": the cart_url render returns a
 *   [data-om-upsell-pending] marker with the newest item's product id, then a
 *   second fetch goes through routes.product_recommendations_url.
 *   intent=complementary is tried FIRST - that is the list merchants curate
 *   in the Shopify Search & Discovery app ("goes well with"), the same source
 *   as the PDP "COMPLETE THE LOOK" block (snippets/product.liquid,
 *   <complementary-products>). Products without curated complementary items
 *   (or whose curation is fully in the cart already) fall back to
 *   intent=related, the algorithmic list, so the row never goes empty.
 * The endpoint renders up to 10 cards; setListContent() trims the list to the
 * block's om_limit (data-om-limit).
 *
 * Arrows: the list is a native horizontal scroller with its scrollbar hidden,
 * so a mouse without a horizontal wheel cannot move it. The heading row
 * carries prev/next buttons (CSS shows them for hover + fine pointers only);
 * updateNav() unhides them while the list overflows and flags the one at
 * either end as aria-disabled - not `disabled`, which would drop keyboard
 * focus out of the drawer when the last card is reached.
 */

(function () {
  'use strict';

  if (window.OmnanaCartUpsell) return;
  window.OmnanaCartUpsell = true;

  var selectors = {
    container: '[data-om-cart-upsell]',
    list: '[data-om-cart-upsell-list]',
    content: '[data-om-cart-upsell-content]',
    pending: '[data-om-upsell-pending]',
    item: '[data-om-upsell-item]',
    nav: '[data-om-cart-upsell-nav]',
    arrow: '[data-om-cart-upsell-arrow]',
  };

  var fetchToken = 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function getContainer() {
    return document.querySelector(selectors.container);
  }

  function withSectionId(url) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=om-api-cart-upsell';
  }

  function updateNav(container) {
    var list = container.querySelector(selectors.list);
    var nav = container.querySelector(selectors.nav);
    if (!list || !nav) return;

    var maxScroll = list.scrollWidth - list.clientWidth;
    nav.toggleAttribute('hidden', maxScroll <= 1);

    var arrows = nav.querySelectorAll(selectors.arrow);
    for (var i = 0; i < arrows.length; i++) {
      var atEnd = arrows[i].getAttribute('data-om-cart-upsell-arrow') === 'prev' ? list.scrollLeft <= 1 : list.scrollLeft >= maxScroll - 1;
      arrows[i].setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }

  function bindNav(container) {
    var list = container.querySelector(selectors.list);
    if (!list || list.omNavBound) return;
    list.omNavBound = true;

    var update = function () {
      updateNav(container);
    };

    list.addEventListener('scroll', update, { passive: true });

    // The list has no width while the drawer is closed, and its width follows
    // the drawer on resize - re-measure whenever its box changes.
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(list);
    }

    update();
  }

  function onArrowClick(event) {
    var arrow = event.target.closest(selectors.arrow);
    if (!arrow || arrow.getAttribute('aria-disabled') === 'true') return;

    var container = arrow.closest(selectors.container);
    var list = container && container.querySelector(selectors.list);
    var item = list && list.querySelector(selectors.item);
    if (!item) return;

    // Move to the neighbouring card's snap point. At the far end the list
    // rests between two snap points (last card right-aligned), hence the
    // ceil/floor instead of a plain +-1 on a rounded index.
    var gap = parseFloat(window.getComputedStyle(list).columnGap) || 0;
    var step = item.getBoundingClientRect().width + gap;
    var now = Date.now();

    // A click while the previous smooth scroll is still running continues
    // from that scroll's target, so quick repeated clicks each move a card.
    var position = now - (list.omNavTime || 0) < 600 ? list.omNavIndex : list.scrollLeft / step;
    var index = arrow.getAttribute('data-om-cart-upsell-arrow') === 'prev' ? Math.ceil(position - 0.05) - 1 : Math.floor(position + 0.05) + 1;
    index = Math.max(0, Math.min(index, Math.ceil((list.scrollWidth - list.clientWidth) / step)));

    list.omNavIndex = index;
    list.omNavTime = now;

    list.scrollTo({
      left: index * step,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  function setListContent(html) {
    var container = getContainer();
    if (!container) return;

    var list = container.querySelector(selectors.list);
    list.innerHTML = html;

    // The API endpoint cannot read the block's om_limit, so it over-renders
    // (up to 10) and the trim happens here.
    var limit = parseInt(container.getAttribute('data-om-limit'), 10) || 4;
    var items = list.querySelectorAll(selectors.item);
    for (var i = limit; i < items.length; i++) {
      items[i].remove();
    }

    var hasItems = list.querySelector(selectors.item) !== null;
    container.toggleAttribute('hidden', !hasItems);

    updateNav(container);
  }

  function extractContent(responseText) {
    var doc = new DOMParser().parseFromString(responseText, 'text/html');
    return doc.querySelector(selectors.content);
  }

  function fetchInto(url, token) {
    return fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        if (token !== fetchToken) return;

        var content = extractContent(responseText);
        setListContent(content ? content.innerHTML : '');
      });
  }

  function refresh() {
    var container = getContainer();
    if (!container) return;

    var token = ++fetchToken;
    var source = container.getAttribute('data-om-source');
    var collectionUrl = container.getAttribute('data-om-collection-url');

    if (source === 'collection' && collectionUrl) {
      fetchInto(withSectionId(collectionUrl), token).catch(function (error) {
        console.log(error);
      });
      return;
    }

    // Recommendations mode: the cart render only carries the pending marker
    // (newest item's product id); the cards come from the second fetch.
    fetch(withSectionId(window.theme.routes.cart_url))
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        if (token !== fetchToken) return;

        var content = extractContent(responseText);
        var pending = content && content.querySelector(selectors.pending);

        if (!pending) {
          setListContent(content ? content.innerHTML : '');
          return;
        }

        var productId = pending.getAttribute('data-product-id');
        if (!productId) {
          setListContent('');
          return;
        }

        // Request the API maximum (10) - the section dedupes against the
        // cart and setListContent() trims to the block's limit.
        var recoBase = withSectionId(pending.getAttribute('data-url')) + '&product_id=' + productId + '&limit=10';

        // Search & Discovery curation first; algorithmic fallback second.
        return fetch(recoBase + '&intent=complementary')
          .then(function (response) {
            return response.text();
          })
          .then(function (complementaryText) {
            if (token !== fetchToken) return;

            var complementaryContent = extractContent(complementaryText);
            if (complementaryContent && complementaryContent.querySelector(selectors.item)) {
              setListContent(complementaryContent.innerHTML);
              return;
            }

            return fetchInto(recoBase + '&intent=related', token);
          });
      })
      .catch(function (error) {
        console.log(error);
      });
  }

  function onCartChange(event) {
    var container = getContainer();
    if (!container) return;

    if (event.detail && event.detail.cartCount === 0) {
      fetchToken++; // invalidate in-flight fetches
      setListContent('');
      return;
    }

    refresh();
  }

  function init() {
    var container = getContainer();
    if (!container) return;

    document.addEventListener('theme:cart:change', onCartChange);
    document.addEventListener('click', onArrowClick);
    bindNav(container);

    // Theme editor re-renders the cart-drawer section without re-running this
    // guarded IIFE - refetch so the row reflects fresh block settings and
    // "recommendations" mode is not stranded on its pending marker (no-op
    // outside the editor: the event never fires).
    document.addEventListener('shopify:section:load', function (event) {
      var reloaded = event.target.querySelector(selectors.container);
      if (reloaded) {
        bindNav(reloaded);
        refresh();
      }
    });

    // The drawer render already contains fresh rows, except in
    // "recommendations" mode where it contains the pending marker.
    if (container.querySelector(selectors.pending)) {
      refresh();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
